"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type {
  CJobOffersContent,
  CJobOffersItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { useCatalogLocation } from "@/lib/cms/useCatalogLocation";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import { Field, Select, SubmitButton } from "@/components/ui/Form";
import {
  cJobOffersTranslations,
  type CJobOffersTranslations,
} from "./CJobOffers.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { CJobOffersArticle } from "./CJobOffersArticle";
import styles from "./CJobOffers.module.css";

/**
 * CJobOffers - visitor view komponentu `c_job_offers` (Oferty pracy).
 *
 * **Data source:** `C_JOB_OFFERS_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   {
 *     id, symbol,
 *     view: 'list' | 'article',
 *     // list view:
 *     items, pagination, filters, available_filters,
 *     // article view:
 *     article, list_href
 *     config: { hide_date, archive_enabled? }
 *   }
 *   ```
 *
 * **Dwa tryby (discriminator `content.view`):**
 *   - `list` - filtry (kategoria / województwo / miasto) + paginowana lista kart
 *   - `article` - patrz `CJobOffersArticle.tsx`
 */
export function CJobOffers({ content }: CJobOffersProps) {
  const t = useT(cJobOffersTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  const hideDate = !!content.config?.hide_date;

  return (
    <div className={styles.root}>
      {content.view === "article" && content.article ? (
        <CJobOffersArticle
          article={content.article}
          listHref={content.list_href ?? null}
          hideDate={hideDate}
        />
      ) : (
        <ListView content={content} hideDate={hideDate} t={t} />
      )}
    </div>
  );
}

export type CJobOffersProps = {
  content: CJobOffersContent;
};

/* ─── List view ─────────────────────────────────────────────────────────── */

function ListView({
  content,
  hideDate,
  t,
}: {
  content: CJobOffersContent;
  hideDate: boolean;
  t: CJobOffersTranslations;
}) {
  const items: CJobOffersItem[] = Array.isArray(content.items) ? content.items : [];
  const pagination = content.pagination;
  const provinceLabels = content.available_filters?.provinces ?? {};
  const categoryLabels = content.available_filters?.categories ?? {};

  return (
    <>
      <FilterBar content={content} t={t} />

      {items.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : (
        <CatalogLoadMore<CJobOffersItem>
          initialItems={items}
          initialPage={pagination?.page ?? 1}
          totalPages={pagination?.total_pages ?? 1}
          totalCount={pagination?.total_count}
          getKey={(item) => item.id}
          extractItems={(c) => {
            const arr = (c as CJobOffersContent | null)?.items;
            return Array.isArray(arr) ? arr : [];
          }}
        >
          {(allItems) => (
            <ul className={styles.list}>
              {allItems.map((item) => (
                <JobCard
                  key={item.id}
                  item={item}
                  hideDate={hideDate}
                  provinceLabels={provinceLabels}
                  categoryLabels={categoryLabels}
                  t={t}
                />
              ))}
            </ul>
          )}
        </CatalogLoadMore>
      )}
    </>
  );
}

function FilterBar({
  content,
  t,
}: {
  content: CJobOffersContent;
  t: CJobOffersTranslations;
}) {
  const router = useRouter();
  const loc = useCatalogLocation();
  const available = content.available_filters;
  const current = content.filters;

  /* Kontrolowane wartości selectów - inicjalizowane stanem z backendu (`filters`). */
  const [category, setCategory] = useState<string>(current?.category ?? "");
  const [province, setProvince] = useState<string>(current?.province ?? "");
  const [city, setCity] = useState<string>(current?.city ?? "");

  const hasActiveFilters =
    !!current?.category || !!current?.province || !!current?.city;

  const buildHref = (cat: string, prov: string, ct: string): string => {
    /* Reset paginacji do strony 1 przy zmianie filtrów. Filtry wysyłamy jako
       query string (`?cat=...&province=...&city=...`) - backend czyta z `$_GET`. */
    const base = loc.buildPageHref(1);
    const params = new URLSearchParams();
    if (cat) params.set("cat", cat);
    if (prov) params.set("province", prov);
    if (ct) params.set("city", ct);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    router.push(buildHref(category, province, city));
  };

  const handleReset = (e: React.MouseEvent) => {
    e.preventDefault();
    setCategory("");
    setProvince("");
    setCity("");
    router.push(loc.buildPageHref(1));
  };

  /* Brak `available_filters` z backendu (starszy payload) -> nie pokazuj filtra. */
  if (!available) return null;

  const categoryOptions = Object.entries(available.categories ?? {});
  const provinceOptions = Object.entries(available.provinces ?? {});
  const cityOptions = Array.isArray(available.cities) ? available.cities : [];

  return (
    <form className={styles.filterForm} onSubmit={handleSubmit}>
      <Field label={t.filterLabelCategory}>
        <Select
          placeholder={t.filterCategory}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {categoryOptions.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
      </Field>

      <Field label={t.filterLabelProvince}>
        <Select
          placeholder={t.filterProvince}
          value={province}
          onChange={(e) => setProvince(e.target.value)}
        >
          {provinceOptions.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
      </Field>

      <Field label={t.filterLabelCity}>
        <Select
          placeholder={t.filterCity}
          value={city}
          onChange={(e) => setCity(e.target.value)}
        >
          {cityOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Field>

      <div className={styles.filterActions}>
        <SubmitButton>{t.filterSubmit}</SubmitButton>
        {hasActiveFilters && (
          <SubmitButton
            variant="link"
            type="button"
            onClick={handleReset}
          >
            {t.filterReset}
          </SubmitButton>
        )}
      </div>
    </form>
  );
}

function JobCard({
  item,
  hideDate,
  provinceLabels,
  categoryLabels,
  t,
}: {
  item: CJobOffersItem;
  hideDate: boolean;
  provinceLabels: Record<string, string>;
  categoryLabels: Record<string, string>;
  t: CJobOffersTranslations;
}) {
  const href = extractInternalHref(item.href);
  const provinceLabel =
    item.province && provinceLabels[item.province]
      ? provinceLabels[item.province]
      : item.province;
  const categoryLabel =
    item.category && categoryLabels[item.category]
      ? categoryLabels[item.category]
      : item.category;

  const cardInner = (
    <>
      {item.picture && (
        <div className={styles.thumbWrap}>
          {/* Native <img> - `picture.thumb_src` jest URL'em do Imperial backend'u.
              Brak Next/Image bo cross-origin + Imperial już generuje miniaturki. */}
          <img
            src={item.picture.thumb_src}
            alt={item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            className={styles.thumb}
            loading="lazy"
          />
        </div>
      )}
      <div className={styles.body}>
        <div className={styles.cardMeta}>
          {!hideDate && item.publication_date && (
            <time
              className={styles.cardMetaItem}
              dateTime={item.publication_date}
            >
              {t.formatDate(item.publication_date)}
            </time>
          )}
          {categoryLabel && (
            <span className={styles.cardMetaItem}>{categoryLabel}</span>
          )}
          {(provinceLabel || item.city) && (
            <span className={styles.cardMetaItem}>
              {[item.city, provinceLabel].filter(Boolean).join(", ")}
            </span>
          )}
        </div>
        <h2 className={styles.cardTitle}>{item.name}</h2>
        {item.reference_number && (
          <div className={styles.cardRef}>
            {t.referenceNumber}: {item.reference_number}
          </div>
        )}
        <span className={styles.cardMore}>{t.readMore}</span>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </Link>
      ) : (
        <a href={item.href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </a>
      )}
    </li>
  );
}
